import type * as io from "io-ts";
import { isLeft } from "fp-ts/lib/Either";
import getIOErrorPaths from "./getIOErrorPaths";
import deepEqual from "fast-deep-equal";

export function safeWrite<T>({
  storage,
  value,
  key,
}: {
  storage: Storage;
  type: io.Type<T>;
  value: T | undefined;
  key: string;
}): void {
  if (value === undefined) {
    storage.removeItem(key);
  } else {
    const asString = JSON.stringify(value);

    if (!deepEqual(value, JSON.parse(asString)))
      console.debug("Cannot safely round-trip to JSON, check for dates, undefined values, etc.", {
        value,
        roundTripValue: JSON.parse(asString),
        key,
        storage,
      });
    storage.setItem(key, asString);
  }
}

export function safeRead<T>({
  storage,
  type,
  key,
}: {
  storage: Storage;
  type: io.Type<T>;
  key: string;
}): T | undefined {
  const valueAsJSON = storage.getItem(key);
  if (valueAsJSON === null) return undefined;
  let value;
  try {
    value = JSON.parse(valueAsJSON);
  } catch {
    console.debug("Cannot JSON.parse", { valueAsJSON, type, storage, key });
    storage.removeItem(key);
    return undefined;
  }

  const validatedValue = type.decode(value);
  if (isLeft(validatedValue)) {
    const errors = getIOErrorPaths(validatedValue);
    const lastError = errors[errors.length - 1];
    console.debug("Wrong format", {
      valueAsJSON,
      value,
      lastError,
      type,
      storage,
      key,
    });
    storage.removeItem(key);
    return undefined;
  }
  return validatedValue.right;
}

const usedKeys: string[] = [];
export function safeKey(key: string) {
  if (usedKeys.includes(key)) {
    const message = `Key "${key}" is already used. \n\nIf you need to access data stored with "${key}" in different components, you must reuse the hook created, not create it twice with the same key.`;

    if (process.env["NODE_ENV"] === "development") {
      // eslint-disable-next-line no-console
      console.warn("Might be happening because of hot-reloading:\n\n", message);
    } else {
      throw new Error(message);
    }
  }
  usedKeys.push(key);
}

const globalMemory = new Map<string, string>();
const memoryStorage: Storage = {
  getItem: (key: string) => {
    const value = globalMemory.get(key);
    return value === undefined ? null : value; // comply with <local|session>Storage APIs
  },
  setItem: (key: string, value: string) => globalMemory.set(key, value),
  removeItem: (key: string) => globalMemory.delete(key),
  clear: () => globalMemory.clear(),
  // Comply with Storage api, unused
  length: 0,
  key: () => null,
};

export const STORAGES = {
  memory: memoryStorage,
  local: window.localStorage,
  session: window.sessionStorage,
};
export type StorageType = keyof typeof STORAGES;
